import { MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Account } from '@/types/api'
import { formatDateTime } from '@/utils/format'
import type { CombinedColumnDef } from '@/api/dowload-excel.api'

interface BuildColumnsOptions {
  pageIndex: number
  pageSize: number
  showUnitColumn: boolean
  canView?: boolean
  canUpdate?: boolean
  canDelete?: boolean
  canResetPass?: boolean
  onViewDetail?: (account: Account) => void
  onUpdate?: (account: Account) => void
  onDelete?: (account: Account) => void
  onToggleStatus?: (account: Account) => void
  onResendActivationMail?: (account: Account) => void
  onResetPassword?: (account: Account) => void
}

export function buildColumns({
  pageIndex,
  pageSize,
  showUnitColumn,
  canView = true,
  canUpdate = true,
  canDelete = true,
  canResetPass = true,
  onViewDetail,
  onUpdate,
  onDelete,
  onToggleStatus,
  onResendActivationMail,
  onResetPassword,
}: BuildColumnsOptions): CombinedColumnDef<Account>[] {
  return [
    {
      id: 'stt',
      header: 'STT',
      size: 55,
      align: 'center',
      cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
    },
    {
      accessorKey: 'fullName',
      header: 'HỌ VÀ TÊN',
      align: 'left',
      cell: ({ row }) => (
        <button
          type="button"
          className="cursor-pointer text-[#CE7A58] hover:text-[#b8694a]"
          onClick={() => onViewDetail?.(row.original)}
        >
          {row.original.fullName}
        </button>
      ),
    },
    {
      accessorKey: 'email',
      header: 'EMAIL',
      align: 'left',
    },
    {
      accessorKey: 'phone',
      header: 'SỐ ĐIỆN THOẠI',
      align: 'left',
    },
    ...(showUnitColumn
      ? [
          {
            accessorKey: 'unitName',
            header: 'ĐƠN VỊ',
            align: 'left',
            cell: ({ getValue }) => {
              const val = getValue() as string
              return val || '-'
            },
          } satisfies CombinedColumnDef<Account>,
        ]
      : []),
    {
      accessorKey: 'groupPermissionName',
      header: 'VAI TRÒ',
      align: 'left',
      cell: ({ getValue }) => {
        const val = getValue() as string
        return val || '-'
      },
    },
    {
      accessorKey: 'updatedAt',
      header: 'NGÀY CẬP NHẬT',
      size: 130,
      align: 'center',
      cell: ({ getValue }) => formatDateTime(getValue()),
    },
    {
      accessorKey: 'status',
      header: 'TRẠNG THÁI',
      size: 110,
      align: 'center',
      cell: ({ row }) => {
        const account = row.original
        return (
          <div className="flex justify-center">
            <Switch
              checked={account.status === 1}
              disabled={!canUpdate}
              onCheckedChange={() => {
                if (!canUpdate) return
                onToggleStatus?.(account)
              }}
            />
          </div>
        )
      },
    },
    {
      id: 'actions',
      size: 55,
      exportable: false,
      meta: {
        fixed: 'right',
      },
      cell: ({ row }) => (
        <div className="flex justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {canUpdate && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onUpdate?.(row.original)}
                >
                  Cập nhật
                </DropdownMenuItem>
              )}
              {canView && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onViewDetail?.(row.original)}
                >
                  Xem chi tiết
                </DropdownMenuItem>
              )}
              {row.original.loginCount === 0 ? (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onResendActivationMail?.(row.original)}
                >
                  Gửi lại mail kích hoạt
                </DropdownMenuItem>
              ) : canResetPass ? (
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => onResetPassword?.(row.original)}
                >
                  Đặt lại mật khẩu mặc định
                </DropdownMenuItem>
              ) : null}
              {canDelete && (
                <DropdownMenuItem
                  className="cursor-pointer text-red-600 focus:text-red-600"
                  onClick={() => onDelete?.(row.original)}
                >
                  Xóa
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ]
}
